
import CaStates from '@/Models/Static/ca_states.json';
import countries from '@/Models/Static/countries.json';
import UsStates from '@/Models/Static/us_states.json';
import OptimizelyService from '@/Services/OptimizelyService';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import StripePaymentMethodService from '@/Services/Subscription/StripePaymentMethodService';
import { Team } from '@/classes/auth';
import HelpTooltip from '@/components/HelpTooltip.vue';
import CountryDropdown from '@/components/Input/CountryDropdown.vue';
import GooglePlacesCombobox from '@/components/Input/GooglePlacesCombobox.vue';
import StateDropdown from '@/components/Input/StateDropdown.vue';
import store from '@/store';
import { ValidationProvider } from 'vee-validate';
import { PropType } from 'vue';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { GooglePlacesSuggestions } from '../Input/GooglePlacesAutocomplete.vue';

@Component({
  components: {
    HelpTooltip,
    StateDropdown,
    CountryDropdown,
    GooglePlacesCombobox,
    ValidationProvider,
  },
  computed: {
    ...mapGetters('location', ['isGeoDetectionAvailable']),
  },
})
export default class AddressWithCombobox extends Vue {
  @Prop({ type: Object as PropType<StripeCheckoutService> })
  stripeCheckoutService!: StripeCheckoutService;

  @Prop({ type: Object as PropType<StripePaymentMethodService> })
  stripePaymentMethodService!: StripePaymentMethodService;

  @Prop({ type: Object as PropType<Team> })
  team!: Team;

  // Just legal entities ready for selection
  legalEntityB2B: string = 'b2b';
  legalEntityB2C: string = 'b2c';

  statesList: Array<any> = [];
  countriesList = countries;
  googlePlacesSuggestions: GooglePlacesSuggestions[] = [];
  googlePlacesAddress: any = '';
  isVatIdNeeded: boolean = false;

  activeTab?: any = 0;
  openVaTModal = false;
  showStateDropdown = false;
  openCountryModal = false;
  isGeoDetectionLoading = false;

  // getter from vuex
  isGeoDetectionAvailable!: boolean;

  store = store;

  async created(): Promise<void> {
    await this.getAllowedPaymentMethodsForUser();

    if (this.team.legal_entity === 'b2c') {
      this.activeTab = 1;
      this.stripeCheckoutService.legalEntity = this.legalEntityB2C;
    }
  }

  mounted(): void {
    this.preloadData();
  }

  updated(): void {
    this.preloadData();
  }

  preloadData(): void {
    // Set list of states for country
    if (this.isUserOnAbtestVariantCNV2936()) {
      const country =
        this.stripeCheckoutService.customer.address.country === ''
          ? this.stripeCheckoutService.country.country_iso2
          : this.stripeCheckoutService.customer.address.country;

      this.stripeCheckoutService.customer.address.country = country;
      this.setStatesList(country);
      this.toggleVatIDField(country);
    } else {
      this.setStatesList(this.stripeCheckoutService.country.country_iso2);
      this.toggleVatIDField(this.stripeCheckoutService.country.country_iso2);
    }
  }

  // Sets the list of states for specific countries
  setStatesList(country: string): void {
    if (country.toLowerCase() === 'us') {
      this.statesList = UsStates;
      this.showStateDropdown = true;
    } else if (country.toLowerCase() === 'ca') {
      this.statesList = CaStates;
      this.showStateDropdown = true;
    } else {
      this.statesList = [];
      this.showStateDropdown = false;
    }
  }

  onOpenCountryModal(): void {
    OptimizelyService.trackEvent(`payment_form_not_your_country`);
    this.openCountryModal = true;
  }

  toggleVatIDField(country: string): void {
    this.isVatIdNeeded = this.stripeCheckoutService.isVatIdNeeded(country);
    if (this.isVatIdNeeded === false) {
      this.stripeCheckoutService.customer.tax_id_data.value = '';
    }
  }

  translateCountriesList(): void {
    const translations: any = this.$t('fields.countries');

    countries.forEach((item) => {
      item.country = translations[item.country_iso2.toLowerCase()];
    });

    this.countriesList = countries;
  }

  onCountrySelected(country: string): void {
    this.stripeCheckoutService.customer.address.country = country;
    OptimizelyService.trackEvent('payment_form_country_change');
  }

  async getAllowedPaymentMethodsForUser(): Promise<void> {
    await this.stripePaymentMethodService.getAllowedPaymentMethodsForUser(
      this.stripeCheckoutService.country.country_iso2
    );
  }

  /**
   * Will take the user vat id input and clean up whitespaces,
   * small case letters and special chars
   */
  vatIdFilter(): void {
    let tmp = this.stripeCheckoutService.customer.tax_id_data.value;

    if (tmp !== null) {
      tmp = tmp.toUpperCase();
      tmp = tmp.replace(/\s/g, '');
      tmp = tmp.replace(/[^A-Z0-9]/g, '');

      // Small delay because two-way binding would otherwise not register the change of value
      setTimeout(() => (this.stripeCheckoutService.customer.tax_id_data.value = tmp), 5);
    }
  }

  // Selects the private-tab and closes the VAT modal
  selectPrivateTab(): void {
    this.activeTab = 1;
    this.stripeCheckoutService.legalEntity = this.legalEntityB2C;
    this.openVaTModal = false;
  }

  onAddressSelected(googlePlacesData: any): void {
    if (googlePlacesData) {
      if (googlePlacesData.street && googlePlacesData.house) {
        this.stripeCheckoutService.customer.address.line1 = `${googlePlacesData.street} ${googlePlacesData.house}`;
      } else if (googlePlacesData.street && !googlePlacesData.house) {
        this.stripeCheckoutService.customer.address.line1 = googlePlacesData.street;
      }
      this.stripeCheckoutService.customer.address.postal_code = googlePlacesData.zip;
      this.stripeCheckoutService.customer.address.city = googlePlacesData.city;
      this.stripeCheckoutService.customer.address.state = googlePlacesData.state;
      if (this.isUserOnAbtestVariantCNV2936()) {
        this.stripeCheckoutService.customer.address.country = googlePlacesData.country_iso;
      }
    }
  }

  onManualEntry(address: string): void {
    this.stripeCheckoutService.customer.address.line1 = address;

    if (address === '') {
      this.stripeCheckoutService.customer.address.postal_code = '';
      this.stripeCheckoutService.customer.address.city = '';
      this.stripeCheckoutService.customer.address.state = '';
    }

    this.googlePlacesAddress = '';
    this.googlePlacesSuggestions = [];
    //@ts-ignore
    window.dataLayer = window.dataLayer || [];
  }

  /**
   * Detects the user's country via geolocation-detection
   */
  async detectCountry(): Promise<void> {
    if (this.isGeoDetectionAvailable) {
      this.isGeoDetectionLoading = true;
      try {
        await this.$store.dispatch('location/detectCountryByGeolocation');
        await this.getAllowedPaymentMethodsForUser();
        this.stripePaymentMethodService.paymentType = 'card'; // When country is changed we put payment type to initial value
        // await this.stripeCheckoutService.updateCustomerVatRate(); // Not sure why this was needed since it updates tax normally
      } catch (e) {
        alert(this.$t('fields.error.location'));
      }
      this.isGeoDetectionLoading = false;
      this.openCountryModal = false;
    }
  }

  isUserOnAbtestVariantCNV2936(): boolean {
    return OptimizelyService.isUserOnAbtestVariantCNV2936();
  }
}
